import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { Popover, PopoverModule } from 'primeng/popover';
import {
  RICH_TEXT_ALIGNMENTS,
  RICH_TEXT_PALETTE,
  RICH_TEXT_PARAGRAPH_STYLES,
  RICH_TEXT_SIZES,
} from '../rich-text-editor.constants';
import {
  RichTextCommand,
  RichTextFormats,
  RichTextTableAction,
  RichTextVariable,
} from '../rich-text-editor.models';

/**
 * Barra de ferramentas do `app-rich-text-editor`, no estilo dos editores de documento: grupos
 * separados, dica em português em cada botão e estado ativo refletindo a formatação do cursor.
 *
 * Só apresenta e emite {@link RichTextCommand}; quem conhece o Quill é o editor. Os botões usam
 * `(mousedown)="$event.preventDefault()"` para não tirar o foco do texto (o Quill perderia a
 * seleção). Menus e popovers roubam o foco mesmo assim, e por isso o editor guarda a última
 * seleção e a devolve antes de aplicar o comando.
 *
 * Botões e menus aqui são `button` nativo + `p-menu`/`p-popover`, e não `app-button`/
 * `app-button-menu`: barra de editor pede botão de ícone compacto (28px) com estado "pressionado",
 * que os wrappers do Design System não têm. Exceção documentada no README do componente.
 */
@Component({
  selector: 'app-rich-text-editor-toolbar',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, MenuModule, PopoverModule],
  templateUrl: './rich-text-editor-toolbar.component.html',
  styleUrls: ['./rich-text-editor-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextEditorToolbarComponent {
  /** Formatação na posição do cursor (`quill.getFormat`). */
  readonly formats = input<RichTextFormats>({});
  /** Variáveis do menu "Variável". Sem variáveis, o botão some. */
  readonly variables = input<RichTextVariable[]>([]);
  readonly fullscreen = input(false);

  readonly command = output<RichTextCommand>();
  readonly toggleFullscreen = output<void>();

  @ViewChild('linkPopover') private linkPopover?: Popover;
  @ViewChild('colorPopover') private colorPopover?: Popover;
  @ViewChild('backgroundPopover') private backgroundPopover?: Popover;
  @ViewChild('tableMenu') private tableMenu?: Menu;

  readonly alignments = RICH_TEXT_ALIGNMENTS;
  readonly palette = RICH_TEXT_PALETTE;

  /** Endereço digitado no popover de link. */
  linkUrl = '';
  readonly linkError = signal('');

  readonly isInTable = computed(() => !!this.formats()['table']);

  readonly paragraphLabel = computed(() => {
    const header = this.formats()['header'] ?? false;
    return RICH_TEXT_PARAGRAPH_STYLES.find(s => s.value === header)?.label ?? 'Texto normal';
  });

  readonly sizeLabel = computed(() => {
    const size = this.formats()['size'] ?? false;
    return RICH_TEXT_SIZES.find(s => s.value === size)?.label ?? 'Normal';
  });

  readonly currentColor = computed(() => this.asString(this.formats()['color']) || '#000000');
  readonly currentBackground = computed(
    () => this.asString(this.formats()['background']) || 'transparent'
  );

  readonly paragraphItems: MenuItem[] = RICH_TEXT_PARAGRAPH_STYLES.map(style => ({
    label: style.label,
    styleClass: style.value ? `rte-menu-heading rte-menu-heading--${style.value}` : '',
    command: () => this.emitFormat('header', style.value),
  }));

  readonly sizeItems: MenuItem[] = RICH_TEXT_SIZES.map(size => ({
    label: size.label,
    command: () => this.emitFormat('size', size.value),
  }));

  /** Itens do menu de tabela; os de linha/coluna só valem com o cursor dentro de uma tabela. */
  readonly tableItems = computed<MenuItem[]>(() => {
    const outside = !this.isInTable();
    const item = (label: string, icon: string, action: RichTextTableAction): MenuItem => ({
      label,
      icon,
      disabled: action !== 'insert' && outside,
      command: () => this.command.emit({ type: 'table', action }),
    });

    return [
      item('Inserir tabela 2 × 2', 'pi pi-table', 'insert'),
      { separator: true },
      item('Inserir linha acima', 'pi pi-arrow-up', 'rowAbove'),
      item('Inserir linha abaixo', 'pi pi-arrow-down', 'rowBelow'),
      item('Inserir coluna à esquerda', 'pi pi-arrow-left', 'columnLeft'),
      item('Inserir coluna à direita', 'pi pi-arrow-right', 'columnRight'),
      { separator: true },
      item('Excluir linha', 'pi pi-minus', 'deleteRow'),
      item('Excluir coluna', 'pi pi-minus', 'deleteColumn'),
      item('Excluir tabela', 'pi pi-trash', 'deleteTable'),
    ];
  });

  readonly variableItems = computed<MenuItem[]>(() =>
    this.variables().map(variable => ({
      label: variable.label,
      title: `Insere {{${variable.key}}} na posição do cursor`,
      icon: 'pi pi-tag',
      command: () => this.command.emit({ type: 'variable', key: variable.key }),
    }))
  );

  /** O formato está ativo no cursor (negrito, lista numerada, alinhamento...). */
  isActive(name: string, value: unknown = true): boolean {
    const current = this.formats()[name];
    if (value === true) return !!current;
    if (value === false) return current === undefined || current === false;
    return current === value;
  }

  /** Liga/desliga um formato booleano (negrito, itálico, citação...). */
  toggle(name: string): void {
    this.emitFormat(name, !this.isActive(name));
  }

  /** Lista: clicar na que já está ativa remove a lista. */
  toggleList(value: 'ordered' | 'bullet'): void {
    this.emitFormat('list', this.isActive('list', value) ? false : value);
  }

  emitFormat(name: string, value: unknown): void {
    this.command.emit({ type: 'format', name, value });
  }

  emit(command: RichTextCommand): void {
    this.command.emit(command);
  }

  pickColor(name: 'color' | 'background', color: string | false): void {
    this.emitFormat(name, color);
    (name === 'color' ? this.colorPopover : this.backgroundPopover)?.hide();
  }

  openTableMenu(event: Event): void {
    this.tableMenu?.toggle(event);
  }

  openLinkPopover(event: Event): void {
    this.linkUrl = this.asString(this.formats()['link']);
    this.linkError.set('');
    this.linkPopover?.toggle(event);
  }

  applyLink(): void {
    const url = this.normalizeUrl(this.linkUrl);
    if (!url) {
      this.linkError.set('Digite um endereço, como https://site.com.br ou um e-mail.');
      return;
    }
    this.command.emit({ type: 'link', url });
    this.linkPopover?.hide();
  }

  removeLink(): void {
    this.command.emit({ type: 'link', url: null });
    this.linkPopover?.hide();
  }

  /** Aceita endereço sem protocolo (vira https) e e-mail solto (vira mailto). */
  private normalizeUrl(raw: string): string {
    const value = (raw ?? '').trim();
    if (!value) return '';
    if (/^(https?:|mailto:)/i.test(value)) return value;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
    return `https://${value}`;
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }
}
